// src/pages/admin/StoreApplications.jsx
// Admin interface to review and approve/reject store applications

import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, serverTimestamp, Timestamp, getDoc, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { ToastContext } from '@/components/ToastProvider';
import { createStore, STORE_TYPES, STORE_MANAGER_ROLES } from '@/lib/storeManagement';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  User, 
  Store, 
  MapPin, 
  Phone, 
  Mail, 
  FileText,
  Eye,
  Calendar,
  DollarSign
} from 'lucide-react';

export default function StoreApplications() {
  const { notify } = React.useContext(ToastContext);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [reviewComments, setReviewComments] = useState('');
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected

  useEffect(() => {
    const q = query(
      collection(db, 'storeApplications'),
      orderBy('submittedAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const apps = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setApplications(apps);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const filteredApplications = applications.filter(app => {
    if (filter === 'all') return true;
    return app.status === filter;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="w-3 h-3 mr-1" />
            Rejected
          </span>
        );
      default:
        return status;
    }
  };

  const getStoreTypeLabel = (type) => {
    switch (type) {
      case 'member':
        return 'Member Store';
      case 'establishment':
        return 'Establishment Store';
      default:
        return type;
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    // Check for unresolved serverTimestamp
    if (timestamp._methodName === 'serverTimestamp') {
      console.log('Unresolved serverTimestamp detected, using current date');
      const now = new Date();
      return now.toLocaleDateString() + ' ' + now.toLocaleTimeString() + ' (estimated)';
    }
    
    try {
      let date;
      
      // Handle Firestore Timestamp object
      if (timestamp instanceof Timestamp) {
        date = timestamp.toDate();
      }
      // Handle Firestore Timestamp with toDate method
      else if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      }
      // Handle Firestore Timestamp (seconds/nanoseconds)
      else if (timestamp.seconds && timestamp.nanoseconds) {
        date = new Date(timestamp.seconds * 1000 + timestamp.nanoseconds / 1000000);
      }
      // Handle regular Date or timestamp
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      // Handle string or number timestamp
      else {
        date = new Date(timestamp);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'Invalid Date';
    }
  };

  const handleReview = (application) => {
    setSelectedApplication(application);
    setReviewComments('');
    setShowModal(true);
  };

  const handleApprove = async () => {
    if (!selectedApplication) return;
    
    setActionLoading(true);
    try {
      // Update application status
      await updateDoc(doc(db, 'storeApplications', selectedApplication.id), {
        status: 'approved',
        reviewedAt: serverTimestamp(),
        reviewedBy: 'admin', // TODO: Get actual admin user ID
        reviewComments: reviewComments.trim(),
        updatedAt: serverTimestamp()
      });

      // Create the store
      const storeData = {
        name: selectedApplication.storeData.name,
        type: selectedApplication.storeType === 'member' ? STORE_TYPES.MEMBER : STORE_TYPES.ESTABLISHMENT,
        description: selectedApplication.storeData.description,
        address: selectedApplication.storeData.address,
        phone: selectedApplication.storeData.phone,
        email: selectedApplication.storeData.email,
        managerUid: selectedApplication.applicantUid,
        status: 'active',
        settings: {
          revenuePercentage: 15, // Default 15%, admin can change later
          operatingHours: {
            monday: { open: '08:00', close: '18:00' },
            tuesday: { open: '08:00', close: '18:00' },
            wednesday: { open: '08:00', close: '18:00' },
            thursday: { open: '08:00', close: '18:00' },
            friday: { open: '08:00', close: '18:00' },
            saturday: { open: '08:00', close: '18:00' },
            sunday: { open: '09:00', close: '17:00' }
          }
        }
      };

      const storeResult = await createStore(storeData);
      
      if (storeResult.success) {
        // Update user's role to store manager (store-specific role)
        const storeSpecificRole = `store_manager_${storeResult.store.id}`;

        // Update user's role in their profile
        try {
          const userRef = doc(db, 'users', selectedApplication.applicantUid);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const existingRoles = Array.isArray(userData.roles) ? userData.roles : [];
            
            // Add the store-specific manager role if not already present
            if (!existingRoles.includes(storeSpecificRole)) {
              await updateDoc(userRef, {
                roles: [...existingRoles, storeSpecificRole],
                updatedAt: serverTimestamp()
              });
            }
          } else {
            // Create user document with the store-specific manager role
            await setDoc(userRef, {
              roles: [storeSpecificRole],
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
          }
        } catch (roleError) {
          console.error('Error updating user role:', roleError);
          // Don't fail the entire approval process for role update errors
        }
        
        notify(`Store application approved and store created successfully!`, { type: 'success' });
        setShowModal(false);
      } else {
        throw new Error(storeResult.error || 'Failed to create store');
      }
    } catch (error) {
      console.error('Error approving application:', error);
      notify(`Failed to approve application: ${error.message}`, { type: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedApplication) return;
    
    setActionLoading(true);
    try {
      await updateDoc(doc(db, 'storeApplications', selectedApplication.id), {
        status: 'rejected',
        reviewedAt: serverTimestamp(),
        reviewedBy: 'admin', // TODO: Get actual admin user ID
        reviewComments: reviewComments.trim(),
        updatedAt: serverTimestamp()
      });

      notify('Store application rejected', { type: 'success' });
      setShowModal(false);
    } catch (error) {
      console.error('Error rejecting application:', error);
      notify(`Failed to reject application: ${error.message}`, { type: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          <span className="ml-2 text-gray-600">Loading applications...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Store Applications</h1>
        <p className="text-sm sm:text-base text-gray-600">Review and approve/reject store applications from members</p>
      </div>

      {/* Filter Tabs - Mobile Optimized */}
      <div className="mb-4 sm:mb-6">
        <div className="border-b border-gray-200">
          {/* Mobile: Scrollable horizontal tabs */}
          <div className="block sm:hidden">
            <nav className="flex space-x-1 overflow-x-auto scrollbar-hide -mb-px">
              {[
                { key: 'all', label: 'All', count: applications.length },
                { key: 'pending', label: 'Pending', count: applications.filter(app => app.status === 'pending').length },
                { key: 'approved', label: 'Approved', count: applications.filter(app => app.status === 'approved').length },
                { key: 'rejected', label: 'Rejected', count: applications.filter(app => app.status === 'rejected').length }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setFilter(tab.key)}
                  className={`flex-shrink-0 py-3 px-3 border-b-2 font-medium text-xs whitespace-nowrap ${
                    filter === tab.key
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </nav>
          </div>
          
          {/* Desktop: Original layout */}
          <div className="hidden sm:block">
            <nav className="-mb-px flex space-x-8">
              {[
                { key: 'all', label: 'All Applications', count: applications.length },
                { key: 'pending', label: 'Pending', count: applications.filter(app => app.status === 'pending').length },
                { key: 'approved', label: 'Approved', count: applications.filter(app => app.status === 'approved').length },
                { key: 'rejected', label: 'Rejected', count: applications.filter(app => app.status === 'rejected').length }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setFilter(tab.key)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    filter === tab.key
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Applications List - Mobile Optimized */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        {filteredApplications.length === 0 ? (
          <div className="p-8 text-center">
            <Store className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No applications found</h3>
            <p className="text-gray-600">
              {filter === 'all' 
                ? 'No store applications have been submitted yet.'
                : `No ${filter} applications found.`
              }
            </p>
          </div>
        ) : (
          <>
            {/* Mobile: Card Layout */}
            <div className="block sm:hidden divide-y divide-gray-200">
              {filteredApplications.map((app) => (
                <div key={app.id} className="p-4">
                  <div className="flex items-start space-x-3 mb-3">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                        <User className="h-5 w-5 text-green-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-sm font-medium text-gray-900 truncate">
                          {app.applicantName}
                        </h3>
                        {getStatusBadge(app.status)}
                      </div>
                      <p className="text-sm text-gray-500 truncate">{app.applicantEmail}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2 mb-3">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">{app.storeData.name}</h4>
                      <p className="text-sm text-gray-500">{getStoreTypeLabel(app.storeType)}</p>
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <MapPin className="w-3 h-3 mr-1" />
                      <span className="truncate">{app.storeData.address}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-gray-900">
                        <DollarSign className="w-4 h-4 mr-1" />
                        ₱{(app.shareCapital || 0).toLocaleString()}
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="w-4 h-4 mr-1" />
                        {formatDate(app.submittedAt)}
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleReview(app)}
                    className="w-full text-green-600 hover:text-green-900 flex items-center justify-center py-2 border border-green-300 rounded-md hover:bg-green-50"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Review Application
                  </button>
                </div>
              ))}
            </div>
            
            {/* Desktop: Table Layout */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Applicant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Store Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Share Capital
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Submitted
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredApplications.map((app) => (
                    <tr key={app.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                              <User className="h-5 w-5 text-green-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {app.applicantName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {app.applicantEmail}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {app.storeData.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {getStoreTypeLabel(app.storeType)}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <MapPin className="w-3 h-3 mr-1" />
                          {app.storeData.address}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex items-center">
                          <DollarSign className="w-4 h-4 mr-1" />
                          ₱{(app.shareCapital || 0).toLocaleString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {formatDate(app.submittedAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(app.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleReview(app)}
                          className="text-green-600 hover:text-green-900 flex items-center"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Review
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Review Modal - Mobile Optimized */}
      {showModal && selectedApplication && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 sm:top-20 mx-auto p-4 sm:p-5 border w-11/12 sm:w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Review Store Application
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Applicant Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Applicant Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Name:</span> {selectedApplication.applicantName}
                    </div>
                    <div>
                      <span className="font-medium">Email:</span> {selectedApplication.applicantEmail}
                    </div>
                    <div>
                      <span className="font-medium">Phone:</span> {selectedApplication.applicantPhone}
                    </div>
                    <div>
                      <span className="font-medium">Member ID:</span> {selectedApplication.memberId}
                    </div>
                    <div>
                      <span className="font-medium">Share Capital:</span> ₱{(selectedApplication.shareCapital || 0).toLocaleString()}
                    </div>
                    <div>
                      <span className="font-medium">Submitted:</span> {formatDate(selectedApplication.submittedAt)}
                    </div>
                  </div>
                </div>

                {/* Store Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Store Information</h4>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="font-medium">Store Name:</span> {selectedApplication.storeData.name}
                    </div>
                    <div>
                      <span className="font-medium">Type:</span> {getStoreTypeLabel(selectedApplication.storeType)}
                    </div>
                    <div>
                      <span className="font-medium">Description:</span> {selectedApplication.storeData.description}
                    </div>
                    <div>
                      <span className="font-medium">Address:</span> {selectedApplication.storeData.address}
                    </div>
                    <div>
                      <span className="font-medium">Phone:</span> {selectedApplication.storeData.phone}
                    </div>
                    <div>
                      <span className="font-medium">Email:</span> {selectedApplication.storeData.email}
                    </div>
                    {selectedApplication.storeData.businessPermit && (
                      <div>
                        <span className="font-medium">Business Permit:</span> {selectedApplication.storeData.businessPermit}
                      </div>
                    )}
                    {selectedApplication.storeData.additionalInfo && (
                      <div>
                        <span className="font-medium">Additional Info:</span> {selectedApplication.storeData.additionalInfo}
                      </div>
                    )}
                  </div>
                </div>

                {/* Review Comments */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Review Comments
                  </label>
                  <textarea
                    value={reviewComments}
                    onChange={(e) => setReviewComments(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    rows={3}
                    placeholder="Add your review comments..."
                  />
                </div>

                {/* Action Buttons - Mobile Optimized */}
                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                  <button
                    onClick={() => setShowModal(false)}
                    className="btn btn-outline w-full sm:w-auto"
                    disabled={actionLoading}
                  >
                    Cancel
                  </button>
                  {selectedApplication.status === 'pending' && (
                    <>
                      <button
                        onClick={handleReject}
                        disabled={actionLoading}
                        className="btn btn-outline text-red-600 border-red-600 hover:bg-red-50 w-full sm:w-auto"
                      >
                        {actionLoading ? 'Rejecting...' : 'Reject'}
                      </button>
                      <button
                        onClick={handleApprove}
                        disabled={actionLoading}
                        className="btn btn-primary w-full sm:w-auto"
                      >
                        {actionLoading ? 'Approving...' : 'Approve'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
